import type { EntityId } from '../sim/entity';
import type { World } from '../sim/world';
import { DEFAULT_SOUND_CONFIG } from '../audio/soundObserver';
import { proximityVolume, tileDistance } from '../audio/proximity';

/**
 * Resolve a duel combat sound's volume (ADR-0131): **full** when the local
 * player is a participant (its own combat stance is on - the sound is their own
 * fight, on top of the listener), else **proximity-scaled** by tile distance
 * to the sounding entity (the footstep/rat-squeal precedent), 0 out of earshot.
 * Shared by the duel installer's hit/death/bow/whiff cues and the death
 * installer's resurrection chime so participant-vs-onlooker mixing can't drift.
 */
export function duelSoundVolume(
  world: World | undefined,
  playerId: EntityId | undefined,
  sourceEntityId: EntityId,
  ceiling: number,
): number {
  const me = playerId !== undefined ? world?.get(playerId) : undefined;
  if (me?.renderable?.combatStance === true) return ceiling; // own fight, full volume
  const meMob = me?.mobile;
  const sourceMob = world?.get(sourceEntityId)?.mobile;
  if (!meMob || !sourceMob) return 0;
  return proximityVolume(
    tileDistance(
      { x: sourceMob.tileX, y: sourceMob.tileY },
      { x: meMob.tileX, y: meMob.tileY },
    ),
    DEFAULT_SOUND_CONFIG.maxTiles,
    ceiling,
  );
}

/**
 * Proximity-scale a world-positioned cue against the local player's live tile
 * (the rat-squeal/vortex-gust/halberd rule): the ceiling at distance 0, fading to
 * 0 out of earshot, and 0 when either tile is unknown (pre-entry, a despawned
 * source). The one distance/config rule every event installer's cue shares.
 */
export function proximityCueVolume(
  world: World | undefined,
  playerId: EntityId | undefined,
  source: EntityId | { x: number; y: number },
  ceiling: number,
): number {
  const meMob = playerId !== undefined ? world?.get(playerId)?.mobile : undefined;
  if (!meMob) return 0;
  const tile =
    typeof source === 'number'
      ? (() => {
          const mob = world?.get(source as EntityId)?.mobile;
          return mob ? { x: mob.tileX, y: mob.tileY } : undefined;
        })()
      : source;
  if (!tile) return 0;
  return proximityVolume(
    tileDistance(tile, { x: meMob.tileX, y: meMob.tileY }),
    DEFAULT_SOUND_CONFIG.maxTiles,
    ceiling,
  );
}

/**
 * The body sex of a duel participant, for the gendered hit-grunt / death-cry
 * pick (male body 400, else female). Absent body reads female, like the cues.
 */
export function bodySexOf(world: World | undefined, entityId: EntityId): number {
  return world?.get(entityId)?.renderable?.body ?? 401;
}
